import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useInView } from '../hooks/useInView'
import { foodMenu, drinksMenu } from '../data/menu'
import { charredOctopus, legendBoard, goatLogo, garlicShrimp, partyBoard, streetFeast } from '../assets/images'

const SIGNATURES = [
  { name: 'Garlic Shrimp',      desc: 'Sautéed shrimp, garlic, olive oil, parsley.',                          price: '€12.00', image: garlicShrimp },
  { name: 'Charred Octopus',    desc: 'Grilled octopus, Mediterranean herbs, lemon dressing.',                price: '€14.00', image: charredOctopus },
  { name: 'GOAT Party Board',   desc: 'Hummus, muhammara, halloumi fries, falafel, anchovies, chicken bites, nachos.', price: '€29.00', image: partyBoard },
  { name: 'GOAT Legend Board',  desc: 'The full spread for four — octopus, shrimp, mezze & more.',            price: '€39.00', image: legendBoard },
]

/* ── Hero ─────────────────────────────────────────────── */

function Hero() {
  const navigate = useNavigate()
  const [ref, inView] = useInView()

  return (
    <section
      className="hero"
      id="top"
      ref={ref}
      style={{ backgroundImage: `radial-gradient(ellipse at center, rgba(230,220,172,0.96) 0%, rgba(230,220,172,0.88) 45%, rgba(230,220,172,0.62) 100%), url(${streetFeast})` }}
    >
      <div className={`hero-badge fade-up${inView ? ' visible' : ''}`}>
        <img src={goatLogo} alt="GOAT" />
      </div>
      <p className={`hero-eyebrow fade-up delay-1${inView ? ' visible' : ''}`}>★&nbsp;&nbsp;Mediterranean Social Club&nbsp;&nbsp;★</p>
      <h1 className={`hero-title fade-up delay-2${inView ? ' visible' : ''}`}>GOAT</h1>
      <p className={`hero-subtitle fade-up delay-3${inView ? ' visible' : ''}`}>Greatest of All Tapas</p>
      <p className={`hero-tagline fade-up delay-4${inView ? ' visible' : ''}`}>One table · endless sharing · Paceville, Malta</p>
      <div className={`hero-actions fade-up delay-5${inView ? ' visible' : ''}`}>
        <button className="btn btn-filled" onClick={() => navigate('/menu')}>See the Menu</button>
        <button className="btn btn-outline" onClick={() => document.getElementById('reserve')?.scrollIntoView({ behavior: 'smooth' })}>
          Contact Us
        </button>
      </div>
    </section>
  )
}

/* ── Concept ──────────────────────────────────────────── */

function Concept() {
  const [ref, inView] = useInView()
  return (
    <section className="concept" id="about" ref={ref}>
      <p className={`eyebrow eyebrow-light fade-up${inView ? ' visible' : ''}`}>The Concept</p>
      <blockquote className={`concept-quote fade-up delay-1${inView ? ' visible' : ''}`}>
        Small plates built for sharing. A bar that runs late. A room that hums till 3AM.
      </blockquote>
      <p className={`concept-body fade-up delay-2${inView ? ' visible' : ''}`}>
        GOAT is Paceville's tapas &amp; cocktail social club — a little bit of everywhere around
        the Mediterranean, brought to one shared table. Pull up a stool, order a spread, and stay a while.
      </p>
    </section>
  )
}

/* ── Signatures ───────────────────────────────────────── */

function Signatures() {
  const navigate = useNavigate()
  const [ref, inView] = useInView()
  return (
    <section className="signatures" id="featured" ref={ref}>
      <h2 className={`section-title fade-up${inView ? ' visible' : ''}`}>The Signatures</h2>
      <p className={`section-italic fade-up delay-1${inView ? ' visible' : ''}`}>Ordered by everyone, every night</p>
      <div className="cards">
        {SIGNATURES.map((item, i) => (
          <div key={item.name} className={`card fade-up delay-${i + 1}${inView ? ' visible' : ''}`}>
            <div className={`card-img${item.image ? ' has-photo' : ''}`}>
              {item.image
                ? <img src={item.image} alt={item.name} loading="lazy" />
                : <span className="card-img-caption">olive ink illustration</span>}
            </div>
            <div className="card-body">
              <p className="card-name">{item.name}</p>
              <p className="card-desc">{item.desc}</p>
              <p className="card-price">{item.price}</p>
            </div>
          </div>
        ))}
      </div>
      <button className="btn btn-outline" onClick={() => navigate('/menu')}>
        Full Food &amp; Drinks Menu →
      </button>
    </section>
  )
}

/* ── Menu Split ───────────────────────────────────────── */

function MenuSplit() {
  const navigate = useNavigate()
  const [ref, inView] = useInView()
  return (
    <div className="menu-split" ref={ref}>
      <button
        className={`menu-half menu-half-light fade-up${inView ? ' visible' : ''}`}
        style={{ textAlign: 'left' }}
        onClick={() => navigate('/menu?tab=food')}
      >
        <p className="menu-cat menu-cat-light">Kitchen</p>
        <h3 className="menu-half-title menu-half-title-light">Food<br />Menu</h3>
        <span className="menu-half-link menu-half-link-light">Tapas, boards, tacos, burgers &amp; sweet endings →</span>
      </button>
      <button
        className={`menu-half menu-half-dark fade-up delay-1${inView ? ' visible' : ''}`}
        style={{ textAlign: 'left' }}
        onClick={() => navigate('/menu?tab=drinks')}
      >
        <p className="menu-cat menu-cat-dark">Bar</p>
        <h3 className="menu-half-title menu-half-title-dark">Drinks<br />Menu</h3>
        <span className="menu-half-link menu-half-link-dark">Signature &amp; classic cocktails, wine, whisky &amp; more →</span>
      </button>
    </div>
  )
}

/* ── Visit ────────────────────────────────────────────── */

function Visit() {
  const [ref, inView] = useInView()
  return (
    <section className="visit" id="visit" ref={ref}>
      <div className="visit-info">
        <h2 className={`visit-title fade-up${inView ? ' visible' : ''}`}>Visit Us</h2>
        <div className={`visit-row fade-up delay-1${inView ? ' visible' : ''}`}>
          <p className="visit-label">Address</p>
          <p className="visit-value">St George's Road, Paceville, St Julian's, Malta</p>
        </div>
        <div className={`visit-row fade-up delay-2${inView ? ' visible' : ''}`}>
          <p className="visit-label">Open</p>
          <p className="visit-value">Every day · 12:00 PM – 3:00 AM</p>
        </div>
        <div className={`visit-row fade-up delay-3${inView ? ' visible' : ''}`}>
          <p className="visit-label">Instagram</p>
          <p className="visit-value">
            <a href="https://instagram.com/goatbar.malta" target="_blank" rel="noopener noreferrer">@goatbar.malta</a>
          </p>
        </div>
      </div>
      <div className="visit-map">
        <iframe
          title="GOAT Bar location"
          src="https://www.google.com/maps?q=GOAT%20Bar%2C%20St%20George%27s%20Road%2C%20St%20Julian%27s%2C%20Malta&output=embed"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </section>
  )
}

/* ── Contact ──────────────────────────────────────────── */

function Contact() {
  const [ref, inView] = useInView()
  const blank = { name: '', email: '', phone: '', subject: 'Event / private party', message: '' }
  const [form, setForm] = useState(blank)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }))
    setErrors(e => ({ ...e, [k]: '' }))
  }

  const validate = () => {
    const e = {}
    if (!form.name.trim())  e.name = 'Name is required'
    if (!form.email.trim()) e.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email'
    if (!form.message.trim()) e.message = 'Message is required'
    return e
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }
    setLoading(true)
    setTimeout(() => { setLoading(false); setSent(true) }, 1500)
  }

  return (
    <section className="contact" id="reserve" ref={ref}>
      <p className={`eyebrow eyebrow-light fade-up${inView ? ' visible' : ''}`}>Get in Touch</p>
      <h2 className={`contact-title fade-up delay-1${inView ? ' visible' : ''}`}>Contact GOAT</h2>
      <p className={`contact-subtitle fade-up delay-2${inView ? ' visible' : ''}`}>
        Planning an event, a private party, or a big group? Drop us a line.
      </p>

      {sent ? (
        <div className="form-success">
          <div className="form-success-icon">✓</div>
          <h3 className="form-success-title">Message Sent!</h3>
          <p className="form-success-body">We'll be in touch shortly.</p>
        </div>
      ) : (
        <form className={`form fade-up delay-3${inView ? ' visible' : ''}`} onSubmit={handleSubmit} noValidate>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="c-name">Name</label>
              <input id="c-name" className={`form-input${errors.name ? ' error' : ''}`} type="text"
                placeholder="Your name" value={form.name} onChange={e => set('name', e.target.value)} autoComplete="name" />
              {errors.name && <span className="form-error-msg">{errors.name}</span>}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="c-email">Email</label>
              <input id="c-email" className={`form-input${errors.email ? ' error' : ''}`} type="email"
                placeholder="you@email.com" value={form.email} onChange={e => set('email', e.target.value)} autoComplete="email" />
              {errors.email && <span className="form-error-msg">{errors.email}</span>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="c-phone">Phone (Optional)</label>
              <input id="c-phone" className="form-input" type="tel"
                placeholder="+356 ..." value={form.phone} onChange={e => set('phone', e.target.value)} autoComplete="tel" />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="c-subject">Subject</label>
              <select id="c-subject" className="form-select form-input" value={form.subject} onChange={e => set('subject', e.target.value)}>
                <option>Event / private party</option>
                <option>Large group booking</option>
                <option>Press / collaboration</option>
                <option>General enquiry</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="c-message">Message</label>
            <textarea id="c-message" className={`form-textarea${errors.message ? ' error' : ''}`}
              placeholder="Tell us about your event, group size, dates, anything…"
              value={form.message} onChange={e => set('message', e.target.value)} />
            {errors.message && <span className="form-error-msg">{errors.message}</span>}
          </div>
          <div className="form-footer">
            <p className="form-note">
              Or reach us on Instagram&nbsp;
              <a href="https://instagram.com/goatbar.malta" target="_blank" rel="noopener noreferrer">@goatbar.malta</a>
            </p>
            <button type="submit" className="btn btn-sand" disabled={loading}>
              {loading ? 'Sending…' : 'Send Message'}
            </button>
          </div>
        </form>
      )}
    </section>
  )
}

/* ── Home page ────────────────────────────────────────── */

export default function Home() {
  const location = useLocation()

  // scroll to section when arriving from another page
  useEffect(() => {
    if (location.state?.scrollTo) {
      const id = location.state.scrollTo
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 80)
    }
  }, [])

  return (
    <>
      <Hero />
      <Concept />
      <Signatures />
      <MenuSplit />
      <Visit />
      <Contact />
    </>
  )
}
